import { useMutation, useQuery } from 'urql';
import { graphql } from '../gql/index.ts';
import { Controller, useForm } from 'react-hook-form';
import {
  FunctionalSettings,
  ModulePrivileges,
  PriceCalculation,
  SeminarOrderBy,
} from '../gql/graphql.ts';
import { Button } from '@progress/kendo-react-buttons';
import React, { useEffect, useRef, useState } from 'react';
import { useKeycloak } from '../shared/auth/KeycloakProvider.tsx';
import { Label } from '@progress/kendo-react-labels';
import { FieldWrapper } from '@progress/kendo-react-form';
import { PageTitle } from './PageTitle.tsx';
import { Fieldset } from './form-components/Fieldset.tsx';
import { CheckboxStateWrapper } from './form-components/CheckboxStateWrapper.tsx';
import { DropdownStateWrapper } from './form-components/DropdownStateWrapper.tsx';
import { InputStateWrapper } from './form-components/InputStateWrapper.tsx';
import { EditorStateWrapper } from './form-components/EditorStateWrapper.tsx';
import { HeroIcon } from '../shared/HeroIcon.tsx';
import { cloneAndRemoveTypename } from '../public/util.ts';
import { Input } from './form-components/Input.tsx';
import { useUserInfo } from '../public/hooks/useUserInfo.ts';
import { useUserCompanies } from './hooks/useUserCompanies.ts';
import { restApiUrl } from '../shared/helpers/backendUrl';

interface FunctionSettingsPageProps {
  companyId: string;
}

const CompanyFunctionQuery = graphql(/* GraphQL */ `
  query companyFunctionSettings($companyId: String!) {
    company(companyId: $companyId) {
      secret
      active
      name
      domain
      functionalSettings {
        errorTextWrongPassword
        infoText
        passwordActive
        priceCalculation
        registrationActive
        searchActive
        seminarsOrderBy
        showPastSeminars
        waitlistActive
        disableUserAccounts
        showBookedOutSeminars
        cancelSignUpActive
        sapCustomerId
        allowedOrigins
      }
      mailSettings {
        receiverSignUpCanceled
      }
    }
  }
`);

const UpdateCompanyFunction = graphql(/* GraphQL */ `
  mutation updateCompanyFunctionSettings(
    $companyId: String!
    $company: UpdateCompanyDto!
  ) {
    updateCompany(companyId: $companyId, company: $company) {
      active
      name
      domain
      functionalSettings {
        errorTextWrongPassword
        infoText
        passwordActive
        priceCalculation
        registrationActive
        searchActive
        seminarsOrderBy
        showPastSeminars
        waitlistActive
        disableUserAccounts
        showBookedOutSeminars
        cancelSignUpActive
        sapCustomerId
        allowedOrigins
      }
    }
  }
`);

interface FormInputs {
  active: boolean;
  name: string;
  domain: string;
  functionalSettings: FunctionalSettings;
}

export const FunctionSettingsPage: React.FC<FunctionSettingsPageProps> = ({
  companyId,
}) => {
  const [{ data, fetching }] = useQuery({
    query: CompanyFunctionQuery,
    variables: { companyId },
    pause: companyId === undefined || companyId === null,
  });
  const { userInfo } = useUserInfo();
  const { refetchCompanies } = useUserCompanies();
  const isSuperUser = userInfo?.data?.currentUser?.privileges?.includes(
    ModulePrivileges.Tenants,
  );

  const [, updateCompany] = useMutation(UpdateCompanyFunction);
  const { token } = useKeycloak();
  const importFileInputRef = useRef<HTMLInputElement>(null);
  const [transferStatus, setTransferStatus] = useState<{
    kind: 'idle' | 'busy' | 'success' | 'error';
    message?: string;
  }>({ kind: 'idle' });

  const handleExportSettings = async () => {
    setTransferStatus({ kind: 'busy', message: 'Exportiere…' });
    try {
      const response = await fetch(
        restApiUrl(`/admin/company/${companyId}/settings/export`),
        {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        },
      );
      if (!response.ok) {
        throw new Error(`Export fehlgeschlagen (${response.status})`);
      }
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      a.download = `company-settings-${companyId}-${timestamp}.json`;
      a.click();
      URL.revokeObjectURL(blobUrl);
      setTransferStatus({
        kind: 'success',
        message: 'Export heruntergeladen.',
      });
    } catch (e) {
      setTransferStatus({
        kind: 'error',
        message: e instanceof Error ? e.message : 'Export fehlgeschlagen',
      });
    }
  };

  const handleImportFileSelected = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    const confirmed = window.confirm(
      'Achtung: Beim Import werden Darstellung, Funktionalität, E-Mail-Einstellungen, ' +
        'öffentliche Seiten, Anmeldefelder und bedingte Felder dieses Unternehmens ' +
        'vollständig überschrieben.\n\n' +
        'Fortfahren?',
    );
    if (!confirmed) return;

    setTransferStatus({ kind: 'busy', message: 'Importiere…' });
    try {
      const text = await file.text();
      let payload: unknown;
      try {
        payload = JSON.parse(text);
      } catch {
        throw new Error('Datei ist kein gültiges JSON.');
      }
      const response = await fetch(
        restApiUrl(`/admin/company/${companyId}/settings/import`),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(payload),
        },
      );
      if (!response.ok) {
        const body = await response.text();
        let message = `Import fehlgeschlagen (${response.status})`;
        try {
          const parsed = JSON.parse(body);
          if (parsed?.message) message = parsed.message;
        } catch {
          // not JSON, keep default
        }
        throw new Error(message);
      }
      setTransferStatus({
        kind: 'success',
        message: 'Import erfolgreich. Seite wird neu geladen…',
      });
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) {
      setTransferStatus({
        kind: 'error',
        message: e instanceof Error ? e.message : 'Import fehlgeschlagen',
      });
    }
  };

  const { control, formState, handleSubmit, reset } = useForm<FormInputs>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      functionalSettings: { infoText: '' },
    },
  });

  const onFormSubmit = (values: FormInputs) => {
    updateCompany({
      companyId,
      company: values,
    }).then((r) => {
      const withoutTypename =
        r.data?.updateCompany && cloneAndRemoveTypename(r.data?.updateCompany);
      if (r.data?.updateCompany && withoutTypename) {
        reset({
          active: withoutTypename.active,
          name: withoutTypename.name,
          domain: withoutTypename.domain,
          functionalSettings: withoutTypename.functionalSettings,
        });
        // Refresh the companies list so the renamed company shows in the
        // header switcher (which reads from companiesForCurrentUser).
        refetchCompanies({ requestPolicy: 'network-only' });
      }
    });
  };

  useEffect(() => {
    if (!fetching) {
      const withoutTypename =
        data?.company && cloneAndRemoveTypename(data.company);

      if (withoutTypename) {
        reset({
          active: withoutTypename.active,
          name: withoutTypename.name,
          domain: withoutTypename.domain,
          functionalSettings: withoutTypename.functionalSettings,
        });
      }
    }
  }, [fetching, JSON.stringify(data?.company)]);

  const { isValid, isDirty } = formState;

  return (
    <>
      <PageTitle title={'Funktion'} />
      <form
        onSubmit={handleSubmit(onFormSubmit)}
        className="k-form k-form-md relative flex w-full flex-col rounded-md bg-white shadow lg:w-full"
      >
        <Fieldset
          legend="Unternehmen"
          description="Name des Unternehmens, wie er in E-Mails und auf der öffentlichen Webseite angezeigt wird."
        >
          <div className="flex flex-col gap-4 md:col-span-9 [&_.k-form-field]:!mt-0">
            <InputStateWrapper
              name={'name'}
              control={control}
              label={'Name'}
              rules={{
                required: 'Name ist erforderlich',
                validate: (value) =>
                  (typeof value === 'string' && value.trim().length > 0) ||
                  'Name ist erforderlich',
              }}
            />
          </div>
        </Fieldset>

        <Fieldset
          legend="Funktion"
          description="Steuern Sie das Verhalten und die aktivierten Funktionen für die öffentliche Webseite."
        >
          <div className="flex flex-col gap-4 md:col-span-9 [&_.k-form-field]:!mt-0">
            <CheckboxStateWrapper
              name={'active'}
              control={control}
              label={'Öffentliche Webseite komplett deaktivieren'}
              inverted
            />
            <CheckboxStateWrapper
              name={'functionalSettings.registrationActive'}
              control={control}
              label={'Teilnehmer können sich an Seminaren anmelden'}
            />
            <CheckboxStateWrapper
              name={'functionalSettings.cancelSignUpActive'}
              control={control}
              label={'Teilnehmer können sich von Seminaren abmelden'}
              hint={
                'Nur möglich, wenn in den E-Mail-Einstellungen ein Empfänger für Abmeldungen gesetzt wurde'
              }
              disabled={!data?.company?.mailSettings?.receiverSignUpCanceled}
            />
            <CheckboxStateWrapper
              name={'functionalSettings.waitlistActive'}
              control={control}
              label={'Warteliste aktiviert'}
            />
            <DropdownStateWrapper
              options={[
                { text: 'Seminarbeginn', value: SeminarOrderBy.SeminarStart },
                { text: 'Seminarende', value: SeminarOrderBy.SeminarEnd },
                { text: 'Seminarnummer', value: SeminarOrderBy.SeminarNumber },
                { text: 'Beschreibung', value: SeminarOrderBy.Description },
              ]}
              name={'functionalSettings.seminarsOrderBy'}
              control={control}
              label={'Seminar Sortierung'}
            />
            <DropdownStateWrapper
              options={[
                { text: 'Keine', value: null },
                {
                  text: 'Preis Auswahl',
                  value: PriceCalculation.PriceSelection,
                },
                {
                  text: 'Gültige Kombinationen',
                  value: PriceCalculation.Combination,
                },
              ]}
              name={'functionalSettings.priceCalculation'}
              control={control}
              label={'Preisberechnung'}
            />
            <CheckboxStateWrapper
              name={'functionalSettings.searchActive'}
              control={control}
              label={'Suche aktiviert'}
            />
            <CheckboxStateWrapper
              name={'functionalSettings.disableUserAccounts'}
              control={control}
              label={'Benutzerkonten komplett deaktivieren (kein Login-Button)'}
            />
            <CheckboxStateWrapper
              name={'functionalSettings.showPastSeminars'}
              control={control}
              label={'Vergangene Seminare auflisten'}
            />
            <CheckboxStateWrapper
              name={'functionalSettings.showBookedOutSeminars'}
              control={control}
              label={'Ausgebuchte Seminare auflisten'}
            />
            <CheckboxStateWrapper
              name={'functionalSettings.passwordActive'}
              control={control}
              label={'Komplette Webseite nur für angemeldete Nutzer erreichbar'}
            />
            <InputStateWrapper
              name={'domain'}
              control={control}
              label={'Domain'}
            />
            <InputStateWrapper
              name={'functionalSettings.errorTextWrongPassword'}
              control={control}
              label={'Fehlermeldung bei falschem Seminar-Passwort'}
            />
            <EditorStateWrapper
              name={'functionalSettings.infoText'}
              control={control}
              label={'Warnhinweis / Infotext'}
            />
            <FieldWrapper>
              <Label>
                Erlaubte Origins (CORS) — eine URL pro Zeile, z.B.
                https://example.com
              </Label>
              <Controller
                render={({ field: { onChange, value, ...rest } }) => (
                  <textarea
                    {...rest}
                    className="k-textarea k-input k-input-md k-rounded-md k-input-solid"
                    rows={4}
                    value={(value ?? []).join('\n')}
                    onChange={(e) => onChange(e.target.value.split('\n'))}
                    onBlur={(e) => {
                      rest.onBlur();
                      onChange(
                        e.target.value
                          .split('\n')
                          .map((s) => s.trim())
                          .filter(Boolean),
                      );
                    }}
                    placeholder="https://example.com"
                  />
                )}
                name={'functionalSettings.allowedOrigins'}
                control={control}
              />
            </FieldWrapper>
            {isSuperUser && (
              <InputStateWrapper
                name={'functionalSettings.sapCustomerId'}
                control={control}
                label={'SAP Kundennummer'}
              />
            )}
          </div>
        </Fieldset>

        <Fieldset
          legend="Einstellungen übertragen"
          description="Exportieren Sie alle Einstellungen dieses Unternehmens als JSON-Datei oder importieren Sie eine zuvor exportierte Datei. Beim Import werden bestehende Einstellungen überschrieben. Anmeldungen, Seminare, Nutzer und das Bannerbild werden nicht übertragen."
        >
          <div className="flex flex-col gap-2 md:col-span-9 [&_.k-form-field]:!mt-0">
            <div className="flex gap-2">
              <Button
                type="button"
                themeColor="base"
                onClick={handleExportSettings}
                disabled={transferStatus.kind === 'busy'}
              >
                Exportieren
              </Button>
              <Button
                type="button"
                themeColor="base"
                onClick={() => importFileInputRef.current?.click()}
                disabled={transferStatus.kind === 'busy'}
              >
                Importieren…
              </Button>
              <input
                ref={importFileInputRef}
                type="file"
                accept="application/json,.json"
                className="hidden"
                onChange={handleImportFileSelected}
              />
            </div>
            {transferStatus.message && (
              <p
                className={
                  transferStatus.kind === 'error'
                    ? 'text-sm text-red-600'
                    : 'text-sm text-gray-600'
                }
              >
                {transferStatus.message}
              </p>
            )}
          </div>
        </Fieldset>

        <Fieldset
          legend="BTS Sync Secret"
          description="Dieses Secret wird benötigt um die Webwelt mit der lokalen BTS-Software zu synchronisieren."
        >
          <div className="flex flex-col gap-4 md:col-span-9 [&_.k-form-field]:!mt-0">
            <Input
              value={data?.company?.secret}
              id="secret"
              label={'Secret'}
              className="cursor-not-allowed"
              readOnly
            />
          </div>
        </Fieldset>

        <div className="k-form-buttons !mt-0 justify-end rounded-md bg-gray-50 !px-8 !py-3 !text-right">
          <Button
            type={'submit'}
            themeColor={'primary'}
            className="!shadow-sm [&>span.k-button-text]:flex [&>span.k-button-text]:items-center [&>span.k-button-text]:gap-1"
            disabled={!isValid || !isDirty}
            title="Speichern"
          >
            <HeroIcon name="CheckCircle" className="block h-5 w-5" />
            <div>Speichern</div>
          </Button>
        </div>
      </form>
    </>
  );
};
